import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import styles from '@/assets/styles/Layout.module.less';
import TopMenu from '@/components/common/TopMenu';
import Footer from '@/components/common/Footer';
import { ReactComponent as Logo } from '@/assets/logo.svg';
import { Link, Outlet } from 'react-router-dom';
import { components } from '@/api/types';
import Cookies from 'js-cookie';
import { validateToken } from '@/libs/api';
import type { MenuProps } from 'antd';
import { Dropdown, Space } from 'antd';
import { DownOutlined, UserOutlined } from '@ant-design/icons';

type UserInfo = components['schemas']['User'];
interface LayoutProps {
  children?: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  useEffect(() => {
    const token = Cookies.get('accessToken');
    if (token) {
      validateToken()
        .then(() => {
          setIsLoggedIn(true);
          const storedUserInfo = localStorage.getItem('userInfo');
          if (storedUserInfo) {
            setUserInfo(JSON.parse(storedUserInfo));
          }
        })
        .catch(() => {
          setIsLoggedIn(false);
          localStorage.removeItem('userInfo');
        });
    } else {
      setIsLoggedIn(false);
      localStorage.removeItem('userInfo');
    }
  }, []);

  const handleLogout = () => {
    Cookies.remove('accessToken');
    Cookies.remove('userId');
    localStorage.removeItem('userInfo');
    setIsLoggedIn(false);
    window.location.reload(); // 强制刷新页面
  };

  const userMenuItems = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'study-center',
        label: <Link to="/study">学习中心</Link>,
      },
      {
        type: 'divider',
      },
      {
        key: 'profile',
        label: <Link to="/profile">账号设置</Link>,
      },
      {
        type: 'divider',
      },
      {
        key: 'change-password',
        label: <Link to="/change-password">修改密码</Link>,
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: '退出登录',
      },
    ],
    [],
  );

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    }
  };

  return (
    <div className={styles.layoutContainer}>
      <header className={styles.layoutHeader}>
        <div className={styles.logoContainer}>
          <Logo className={styles.logo} />
        </div>
        <TopMenu isLoggedIn={isLoggedIn} userInfo={userInfo} />
        <div className={styles.headerRight}>
          {isLoggedIn && userInfo ? (
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              trigger={['click']}
              placement="bottomRight"
            >
              <a
                className={styles.userDropdownTrigger}
                onClick={e => {
                  e.preventDefault();
                }}
              >
                <Space size={6}>
                  <UserOutlined />
                  <span className={styles.notDisplayInMobile}>
                    欢迎, {userInfo?.name || '用户'}
                  </span>
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          ) : (
            <div className={styles.authLinks}>
              <span>
                <Link to="/register">注册</Link>
              </span>
              <span> | </span>
              <span>
                <Link to="/login">登录</Link>
              </span>
            </div>
          )}
        </div>
      </header>
      <main className={styles.layoutContent}>{children || <Outlet />}</main>
      <Footer />
    </div>
  );
};

export default Layout;
